import { Component } from '@theme/component';

/**
 * Tabbed content with clickable image-driven quotes.
 *
 * Markup expectations (set by the Liquid section/blocks):
 * - <button ref="tabButtons[]" data-tab-target="<panelId>"> in the tab list
 * - <div ref="panels[]" data-tab-id="<panelId>"> for each panel
 * - <div ref="quotes[]" data-quote-for="<panelId>"> inside each panel
 * - <button ref="tabItems[]" data-item-id="<itemId>"> for each image item
 * - <template ref="itemQuotes[]" data-quote-id="<itemId>"> beside each item
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} [tabButtons]
 * @property {HTMLElement[]} [panels]
 * @property {HTMLElement[]} [quotes]
 * @property {HTMLElement[]} [tabItems]
 * @property {HTMLTemplateElement[]} [itemQuotes]
 *
 * @extends Component<Refs>
 */
class TabbedContentComponent extends Component {
  connectedCallback() {
    super.connectedCallback();

    // Lift tab buttons that were rendered inside each block (next to their
    // panel) into the dedicated tab rail at the top of the component. We have
    // to render the buttons inside the block files because this theme's blocks
    // framework does not expose child-block `.settings` when iterated via
    // `section.blocks` in a section file — `block.settings.title` only resolves
    // when read from within the block file's own render context.
    this.#liftTabButtons();

    this.addEventListener('click', this.#handleClick);
    this.addEventListener('keydown', this.#handleKeydown);

    this.#activateInitial();
  }

  #liftTabButtons() {
    const rail =
      this.querySelector('[ref="tabList"]') ??
      this.querySelector('.tabbed-content__tabs');
    if (!(rail instanceof HTMLElement)) return;

    // Capture in document order so the rail preserves the editor's block order.
    const buttons = Array.from(this.querySelectorAll('[data-tab-button]'));
    let moved = false;
    for (const button of buttons) {
      if (button.parentElement === rail) continue;
      rail.appendChild(button);
      moved = true;
    }

    // The Component base class refreshes `this.refs` asynchronously via a
    // MutationObserver. Force a synchronous refresh so #activateInitial reads
    // the buttons in their new home.
    if (moved && typeof this.updatedCallback === 'function') {
      this.updatedCallback();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeydown);
  }

  #handleClick = (/** @type {Event} */ event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    if (!target) return;

    const tabButton = target.closest('[data-tab-target]');
    if (tabButton instanceof HTMLElement && this.contains(tabButton)) {
      const panelId = tabButton.dataset.tabTarget;
      if (panelId) this.#activateTab(panelId);
      return;
    }

    const itemButton = target.closest('[data-item-id]');
    if (itemButton instanceof HTMLElement && this.contains(itemButton)) {
      const itemId = itemButton.dataset.itemId;
      if (itemId) this.#activateItem(itemId);
    }
  };

  #handleKeydown = (/** @type {KeyboardEvent} */ event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    if (!(target instanceof HTMLElement)) return;
    if (!target.matches('[data-tab-target]')) return;

    const buttons = this.refs.tabButtons ?? [];
    if (buttons.length < 2) return;

    const currentIndex = buttons.indexOf(target);
    if (currentIndex === -1) return;

    let nextIndex = currentIndex;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        nextIndex = (currentIndex + 1) % buttons.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        nextIndex = (currentIndex - 1 + buttons.length) % buttons.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = buttons.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    const next = buttons[nextIndex];
    if (next instanceof HTMLElement) {
      next.focus();
      const panelId = next.dataset.tabTarget;
      if (panelId) this.#activateTab(panelId);
    }
  };

  #activateInitial() {
    const panels = this.refs.panels ?? [];
    const firstPanel = panels[0];
    if (!(firstPanel instanceof HTMLElement)) return;

    const panelId = firstPanel.dataset.tabId;
    if (panelId) this.#activateTab(panelId, { focus: false });
  }

  /**
   * @param {string} panelId
   * @param {{ focus?: boolean }} [options]
   */
  #activateTab(panelId, options = {}) {
    const buttons = this.refs.tabButtons ?? [];
    const panels = this.refs.panels ?? [];

    for (const button of buttons) {
      if (!(button instanceof HTMLElement)) continue;
      const isActive = button.dataset.tabTarget === panelId;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-selected', isActive ? 'true' : 'false');
      button.tabIndex = isActive ? 0 : -1;
    }

    for (const panel of panels) {
      if (!(panel instanceof HTMLElement)) continue;
      panel.classList.toggle('is-active', panel.dataset.tabId === panelId);
    }

    this.dataset.activeTab = panelId;

    if (options.focus) {
      const activeButton = buttons.find(
        (button) => button instanceof HTMLElement && button.dataset.tabTarget === panelId
      );
      if (activeButton instanceof HTMLElement) activeButton.focus();
    }

    const activePanel = panels.find(
      (panel) => panel instanceof HTMLElement && panel.dataset.tabId === panelId
    );
    if (activePanel instanceof HTMLElement) {
      const firstItem = activePanel.querySelector('[data-item-id]');
      if (firstItem instanceof HTMLElement && firstItem.dataset.itemId) {
        this.#activateItem(firstItem.dataset.itemId, { panel: activePanel });
      }
    }
  }

  /**
   * @param {string} itemId
   * @param {{ panel?: HTMLElement }} [options]
   */
  #activateItem(itemId, options = {}) {
    const items = this.refs.tabItems ?? [];
    const quoteTemplates = this.refs.itemQuotes ?? [];
    const quotes = this.refs.quotes ?? [];

    const activeItem = items.find(
      (item) => item instanceof HTMLElement && item.dataset.itemId === itemId
    );
    if (!(activeItem instanceof HTMLElement)) return;

    const panel = options.panel ?? activeItem.closest('.tab-panel');
    if (!(panel instanceof HTMLElement)) return;

    const panelItems = items.filter(
      (item) => item instanceof HTMLElement && panel.contains(item)
    );
    for (const item of panelItems) {
      item.classList.toggle('is-active', item === activeItem);
      item.setAttribute('aria-pressed', item === activeItem ? 'true' : 'false');
    }

    const template = quoteTemplates.find(
      (node) =>
        node instanceof HTMLTemplateElement &&
        node.dataset.quoteId === itemId &&
        panel.contains(node)
    );

    const quoteHost = quotes.find(
      (node) =>
        node instanceof HTMLElement &&
        node.dataset.quoteFor === panel.dataset.tabId
    );

    if (
      template instanceof HTMLTemplateElement &&
      quoteHost instanceof HTMLElement
    ) {
      let inner = quoteHost.querySelector('.tab-panel__quote-inner');
      if (!(inner instanceof HTMLElement)) {
        inner = document.createElement('div');
        inner.className = 'tab-panel__quote-inner';
        quoteHost.replaceChildren(inner);
      }
      const fragment = template.content.cloneNode(true);
      inner.replaceChildren(fragment);
      inner.style.animation = 'none';
      void inner.offsetWidth;
      inner.style.animation = '';
    }
  }
}

if (!customElements.get('tabbed-content-component')) {
  customElements.define('tabbed-content-component', TabbedContentComponent);
}
